import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react"

interface TestSetSaveState {
  isSaving: boolean
  setIsSaving: (value: boolean) => void
  isSaveInFlight: () => boolean
  registerApiSetupDraft: () => () => void
  hasApiSetupDraft: () => boolean
}

const TestSetSaveContext = createContext<TestSetSaveState | undefined>(
  undefined,
)

export function TestSetSaveProvider({ children }: { children: ReactNode }) {
  const [isSaving, setSaving] = useState(false)
  const savingRef = useRef(false)
  const apiDraftsRef = useRef(new Set<symbol>())
  const registerApiSetupDraft = useCallback(() => {
    const token = Symbol("API Setup draft")
    apiDraftsRef.current.add(token)
    return () => {
      apiDraftsRef.current.delete(token)
    }
  }, [])
  const hasApiSetupDraft = useCallback(() => apiDraftsRef.current.size > 0, [])
  const setIsSaving = useCallback((value: boolean) => {
    savingRef.current = value
    setSaving(value)
  }, [])
  const isSaveInFlight = useCallback(() => savingRef.current, [])
  const value = useMemo(
    () => ({
      isSaving,
      setIsSaving,
      isSaveInFlight,
      registerApiSetupDraft,
      hasApiSetupDraft,
    }),
    [
      isSaving,
      setIsSaving,
      isSaveInFlight,
      registerApiSetupDraft,
      hasApiSetupDraft,
    ],
  )

  return (
    <TestSetSaveContext.Provider value={value}>
      {children}
    </TestSetSaveContext.Provider>
  )
}

export function useTestSetSaveState() {
  const context = useContext(TestSetSaveContext)
  if (!context) {
    throw new Error(
      "useTestSetSaveState must be used within TestSetSaveProvider",
    )
  }
  return context
}
